/* Elin Perfumes — brand word cloud.
 *
 * Packs brand names by catalogue depth using an Archimedean spiral with
 * collision rejection, falling back to a grid scan for anything the spiral
 * cannot seat. Sizes are emitted in cqw so the cloud scales with its
 * container rather than the viewport.
 *
 * Timings mirror the design prototype: words pop in on a 0.11s stagger, every
 * sixth word breathes on a long pulse, and the whole cloud replays five
 * seconds after the last word lands. Placement is solved once and cached —
 * only the elements are rebuilt per cycle, so a replay costs nothing.
 */
(() => {
  const W = 1100;
  const H = 280;
  const CX = W / 2;
  const CY = H / 2;
  const STAGGER = 0.11;
  const POP_DELAY = 0.15;
  const POP_DURATION = 0.5;
  const HOLD_AFTER_COMPLETE = 5000;

  const parse = (raw) =>
    raw
      .split(/[\n,]/)
      .map((entry) => entry.trim())
      .filter(Boolean)
      .map((entry) => {
        const at = entry.lastIndexOf(':');
        if (at < 1) return null;
        const name = entry.slice(0, at).trim();
        const count = Number(entry.slice(at + 1).trim());
        return name && Number.isFinite(count) && count > 0 ? { name, count } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.count - a.count);

  /** Solve positions once; returns render-ready descriptors. */
  function solve(brands) {
    const placed = [];
    const ctx = document.createElement('canvas').getContext('2d');
    const hits = (r, gap) =>
      r.x < 0 ||
      r.y < 0 ||
      r.x + r.w > W ||
      r.y + r.h > H ||
      placed.some((p) => r.x < p.x + p.w + gap && r.x + r.w + gap > p.x && r.y < p.y + p.h + gap && r.y + r.h + gap > p.y);

    const out = [];

    brands.forEach((brand, i) => {
      const weight = Math.min(1, brand.count / 60);
      const size = Math.round(6 + Math.sqrt(brand.count) * 3.6);
      const vertical = i > 10 && i % 4 === 2;

      ctx.font = `900 ${size}px Inter, sans-serif`;
      const metrics = ctx.measureText(brand.name);
      const textW = metrics.width + 2;
      const textH = (metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent || size * 1.25) + 1;
      const w = vertical ? textH : textW;
      const h = vertical ? textW : textH;

      let x = null;
      let y = null;

      for (const gap of [3, 2, 1]) {
        for (let step = 0; step < 12000; step++) {
          const angle = step * 0.07;
          const radius = angle * 0.55;
          if (radius > 900) break;
          const phase = i * 2.4;
          const candidate = {
            x: CX + radius * Math.cos(angle + phase) * 1.35 - w / 2,
            y: CY + radius * Math.sin(angle + phase) * 0.62 - h / 2,
            w,
            h,
          };
          if (!hits(candidate, gap)) {
            x = candidate.x;
            y = candidate.y;
            break;
          }
        }
        if (x !== null) break;
      }

      if (x === null) {
        outer: for (let gy = 0; gy <= H - h; gy += 3) {
          for (let gx = 0; gx <= W - w; gx += 3) {
            if (!hits({ x: gx, y: gy, w, h }, 1)) {
              x = gx;
              y = gy;
              break outer;
            }
          }
        }
      }

      if (x === null) return;
      placed.push({ x, y, w, h });

      out.push({
        name: brand.name,
        left: ((x + w / 2) / W) * 100,
        top: ((y + h / 2) / H) * 100,
        fontSize: (size / W) * 100,
        color: `oklch(${Math.round(68 - 14 * weight)}% ${(0.07 + 0.23 * weight).toFixed(2)} ${285 + ((i * 17) % 65)})`,
        vertical,
        index: i,
      });
    });

    return out;
  }

  /* Three nested spans, as in the prototype: the outer one positions and
     rotates, the middle one pops in, the inner one carries type and pulse.
     Splitting them keeps the pop's scale from clobbering the rotation. */
  function render(host, placements) {
    const frag = document.createDocumentFragment();

    placements.forEach((p) => {
      const outer = document.createElement('span');
      outer.className = 'elin-cloud__word';
      outer.style.left = `${p.left}%`;
      outer.style.top = `${p.top}%`;
      if (p.vertical) outer.style.transform = 'translate(-50%, -50%) rotate(90deg)';

      const pop = document.createElement('span');
      pop.className = 'elin-cloud__pop';
      pop.style.animationDelay = `${(POP_DELAY + p.index * STAGGER).toFixed(2)}s`;

      const inner = document.createElement('span');
      inner.className = 'elin-cloud__ink';
      inner.textContent = p.name;
      inner.style.fontSize = `${p.fontSize}cqw`;
      inner.style.color = p.color;
      if (p.index % 6 === 0) {
        inner.style.animation = `elinPulse ${14 + (p.index % 4) * 4}s ease-in-out ${(p.index * STAGGER).toFixed(2)}s infinite`;
      }

      pop.appendChild(inner);
      outer.appendChild(pop);
      frag.appendChild(outer);
    });

    host.replaceChildren(frag);
  }

  document.querySelectorAll('[data-elin-cloud]').forEach((host) => {
    const brands = parse(host.dataset.elinCloud || '');
    if (!brands.length) return;

    let placements = null;
    let timer = null;

    const cycleLength = (POP_DELAY + brands.length * STAGGER + POP_DURATION) * 1000 + HOLD_AFTER_COMPLETE;

    const paint = () => {
      if (!placements) placements = solve(brands);
      render(host, placements);
    };

    const loop = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        paint();
        loop();
      }, cycleLength);
    };

    const start = () => {
      paint();
      // Metrics taken before the webfont lands are wrong; re-solve once ready.
      if (document.fonts?.ready) {
        document.fonts.ready.then(() => {
          placements = null;
          paint();
        });
      }
      loop();
    };

    if (!('IntersectionObserver' in window)) {
      start();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          start();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(host);
  });

  /* Heading that alternates between two lines on a fixed interval. */
  document.querySelectorAll('[data-elin-swap]').forEach((el) => {
    let lines;
    try {
      lines = JSON.parse(el.dataset.elinSwap);
    } catch {
      return;
    }
    if (!Array.isArray(lines) || lines.length < 2) return;

    const every = Number(el.dataset.elinSwapEvery || 4000);
    let i = 0;

    setInterval(() => {
      i = (i + 1) % lines.length;
      el.classList.add('is-swapping');
      setTimeout(() => {
        el.textContent = lines[i];
        el.classList.remove('is-swapping');
      }, 260);
    }, every);
  });
})();
